import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import '../pages/Login.css';
import './PartnerAuth.css';

const RESEND_SECONDS = 30;

/**
 * The phone + OTP screen shared by the partner login (/owner) and the
 * registration journey (/list-your-property). Same layout and styles as the
 * admin sign-in (/sy-admin/login): brand panel on the left, form on the
 * right. The page owns the state and the API calls; this only draws it.
 *
 * side:     { line, points: [string] }   brand panel copy
 * card:     { title, text }              form heading for the phone step
 * footer:   { text, to, link }           "switch" line under the form
 * progress: optional "Step 1 of 4" line above the heading
 */
const PartnerAuth = ({
  side,
  card,
  footer,
  progress,
  step,
  phone,
  otp,
  loading,
  error,
  message,
  onPhoneChange,
  onOtpChange,
  onSendOtp,
  onVerifyOtp,
  onResendOtp,
  onChangePhone,
}) => {
  const { companyName, logoUrl, loading: settingsLoading } = useSettings();
  const brand = settingsLoading ? '' : companyName || 'WeOtp';
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (step === 'otp') setResendIn(RESEND_SECONDS);
  }, [step]);

  useEffect(() => {
    if (step !== 'otp' || resendIn <= 0) return undefined;
    const timer = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(timer);
  }, [step, resendIn]);

  const digits = String(phone || '').replace(/\D/g, '');
  const displayPhone = digits.length > 10 ? `+${digits}` : `+91 ${digits}`;

  const mark = (compact) => (
    <div className={`login-mark ${compact ? 'login-mark-compact' : ''}`}>
      {logoUrl ? <img src={logoUrl} alt="" /> : <span aria-hidden="true">{(brand || 'W').charAt(0)}</span>}
    </div>
  );

  return (
    <div className="login partner-login">
      <aside className="login-brand">
        <span className="login-glow login-glow-a" aria-hidden="true" />
        <span className="login-glow login-glow-b" aria-hidden="true" />
        <div className="login-brand-inner">
          {mark(false)}
          <h1>{brand || ' '}</h1>
          <p className="login-brand-line">{side.line}</p>
          <ul className="login-points">
            {side.points.map((point) => <li key={point}>{point}</li>)}
          </ul>
        </div>
        <p className="login-brand-foot">
          <Link to="/">← Back to {brand}</Link>
          <span>&copy; {new Date().getFullYear()} {brand}</span>
        </p>
      </aside>

      <main className="login-panel">
        <div className="login-form-wrap">
          {mark(true)}
          {progress && <p className="partner-login-step">{progress} · {step === 'phone' ? 'Phone' : 'Verify'}</p>}

          {step === 'phone' ? (
            <>
              <h2>{card.title}</h2>
              <p className="login-sub">{card.text}</p>
              <form onSubmit={onSendOtp} noValidate>
                <div className="form-group">
                  <label htmlFor="partner-phone">Phone number</label>
                  <div className="partner-login-phone">
                    <span>+91</span>
                    <input
                      id="partner-phone"
                      type="tel"
                      inputMode="numeric"
                      value={phone}
                      onChange={(event) => onPhoneChange(event.target.value.replace(/[^\d+\s]/g, ''))}
                      placeholder="98765 43210"
                      autoComplete="tel"
                      autoFocus
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
                <div aria-live="polite">
                  {error && <div className="error-message login-error">{error}</div>}
                  {message && <div className="partner-login-ok login-error">{message}</div>}
                </div>
                <button type="submit" className="btn btn-primary login-submit" disabled={loading}>
                  {loading && <span className="login-spinner" aria-hidden="true" />}
                  {loading ? 'Sending OTP…' : 'Send OTP'}
                </button>
              </form>
              <p className="login-help">
                By continuing you agree to our <Link to="/page/terms-and-conditions">Terms</Link> and{' '}
                <Link to="/page/privacy-policy">Privacy Policy</Link>.
              </p>
              {footer && (
                <p className="partner-login-switch">
                  <span>{footer.text}</span>
                  <Link to={footer.to}>{footer.link}</Link>
                </p>
              )}
            </>
          ) : (
            <>
              <h2>Enter the OTP</h2>
              <p className="login-sub">
                We sent a 6-digit code to <strong>{displayPhone}</strong>.{' '}
                <button type="button" className="partner-login-link" onClick={onChangePhone} disabled={loading}>Change</button>
              </p>
              <form onSubmit={onVerifyOtp} noValidate>
                <div className="form-group">
                  <label htmlFor="partner-otp">One-time password</label>
                  <input
                    id="partner-otp"
                    className="partner-login-otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={otp}
                    onChange={(event) => onOtpChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="••••••"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    disabled={loading}
                  />
                </div>
                <div aria-live="polite">
                  {error && <div className="error-message login-error">{error}</div>}
                  {message && <div className="partner-login-ok login-error">{message}</div>}
                </div>
                <button type="submit" className="btn btn-primary login-submit" disabled={loading || String(otp).length < 4}>
                  {loading && <span className="login-spinner" aria-hidden="true" />}
                  {loading ? 'Verifying…' : 'Verify & continue'}
                </button>
              </form>
              <p className="login-help">
                {resendIn > 0 ? (
                  <>Resend OTP in 0:{String(resendIn).padStart(2, '0')}</>
                ) : (
                  <button type="button" className="partner-login-link" onClick={() => { onResendOtp(); setResendIn(RESEND_SECONDS); }} disabled={loading}>
                    Resend OTP
                  </button>
                )}
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default PartnerAuth;
